/**
 * G5 — tedarik ve ürün veri modeli sözleşmeleri (docs/tasarim/tedarik-urun-veri-modeli-onerileri.md; migration 0300–0305).
 *
 *  1. Ürün / hizmet türü (`productKind`): 'product' | 'service' | null (BELİRTİLMEMİŞ — eski kayıtlar tahminle doldurulmaz).
 *  2. Tedarikçi ↔ ürün fiyatı: tedarikçi şirket + ürün + birim fiyat + para birimi + geçerlilik + termin; geçmiş korunur.
 *  3. Tedarikçi siparişi karşılama: ürün satırında kısmi MAL KABULÜ (miktar/tarih/kabul eden; eksik miktar), hizmet satırında
 *     AŞAMALAR (planlandı → başladı → teslim edildi → onaylandı). Mal ve hizmet akışı ayrıdır; türü belirtilmemiş satırda ikisi de
 *     yazılmaz.
 *  4. İade / uygunsuzluk (satır, miktar, neden, durum; iade kararı stoklu üründe stok çıkışı).
 *  5. Tedarik teklifleri: aynı ihtiyaca birden çok tedarikçi teklifi; para birimleri açıklamasız karşılaştırılmaz (kur kaynağı
 *     yok → "farklı para birimi" uyarısı); seçilen teklif + zorunlu gerekçe → tedarikçi siparişi.
 *  6. Muhasebe bağlantı sözleşmesi: dış yetkili kaynaktan İMZALI alım (fatura/ödeme gerçeği). Castinflow hesaplamaz; kaynak bağlı
 *     değilse "—" + neden.
 */
import { z } from 'zod';
import { MONEY_POLICY, QUANTITY_POLICY, currencySchema, decCmp, decimalString, parseDecimal, } from './decimal.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
/** İstemcinin işlem başına ürettiği anahtar (çift tıklama / yeniden gönderim tek kayıt) */
export const requestKeySchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{8,120}$/, 'geçersiz işlem anahtarı');
const money = decimalString(MONEY_POLICY);
const positiveQuantity = decimalString({ ...QUANTITY_POLICY, min: '0', nonZero: true });
const leadDays = z.number().int().min(0).max(3650);
// ---------------------------------------------------------------------------------------------------------------------
// 1) Ürün / hizmet türü
// ---------------------------------------------------------------------------------------------------------------------
export const PRODUCT_KINDS = ['product', 'service'];
export const PRODUCT_KIND_LABELS = {
    product: 'Ürün',
    service: 'Hizmet',
};
/** Liste süzgecinde "türü belirtilmemiş" değeri (`productKind is_null`) */
export const PRODUCT_KIND_UNSPECIFIED_LABEL = 'Türü belirtilmemiş';
// ---------------------------------------------------------------------------------------------------------------------
// 2) Tedarikçi fiyatı
// ---------------------------------------------------------------------------------------------------------------------
export const supplierPriceInputSchema = z
    .object({
    supplierId: uuid,
    unitPrice: money,
    currency: currencySchema,
    validFrom: isoDate.optional(),
    validTo: isoDate.nullable().optional(),
    leadTimeDays: leadDays.nullable().optional(),
    minQuantity: positiveQuantity.nullable().optional(),
    note: optText(1000).optional(),
    requestKey: requestKeySchema,
})
    .strict()
    .refine((v) => !v.validFrom || !v.validTo || v.validTo >= v.validFrom, {
    message: 'bitiş başlangıçtan önce olamaz',
    path: ['validTo'],
});
export const endSupplierPriceInputSchema = z.object({ validTo: isoDate.optional() }).strict();
export const SUPPLIER_PRICE_STATES = ['current', 'upcoming', 'expired', 'ended'];
export const SUPPLIER_PRICE_STATE_LABELS = {
    current: 'Geçerli',
    upcoming: 'Başlamadı',
    expired: 'Süresi doldu',
    ended: 'Sonlandırıldı',
};
export const supplierPriceViewSchema = z.object({
    id: z.string(),
    supplierId: z.string(),
    /** Çağıranın göremediği tedarikçide ad dönmez (satır da dönmez — görünmeyen kayıt sızmaz) */
    supplierName: z.string(),
    productId: z.string(),
    unitPrice: z.string(),
    currency: z.string(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    leadTimeDays: z.number().int().nullable(),
    minQuantity: z.string().nullable(),
    source: z.enum(['manual', 'quote']),
    sourceQuoteId: z.string().nullable(),
    note: z.string().nullable(),
    state: z.enum(SUPPLIER_PRICE_STATES),
    createdBy: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    endedAt: z.string().nullable(),
});
export const supplierPriceListSchema = z.object({
    today: z.string(),
    items: z.array(supplierPriceViewSchema),
});
/** Geçerlilik durumu (kiracı takvim gününe göre) */
export function supplierPriceState(p, today) {
    if (p.endedAt !== null)
        return 'ended';
    if (p.validFrom > today)
        return 'upcoming';
    if (p.validTo !== null && p.validTo < today)
        return 'expired';
    return 'current';
}
// ---------------------------------------------------------------------------------------------------------------------
// 3–4) Tedarikçi siparişi karşılama: mal kabulü, hizmet aşaması, uygunsuzluk
// ---------------------------------------------------------------------------------------------------------------------
export const RECEIPT_STATES = ['not_applicable', 'none', 'partial', 'received'];
export const RECEIPT_STATE_LABELS = {
    not_applicable: 'Ürün satırı yok',
    none: 'Teslim alınmadı',
    partial: 'Kısmen teslim alındı',
    received: 'Tamamı teslim alındı',
};
export const SERVICE_STATES = [
    'not_applicable',
    'no_milestones',
    'in_progress',
    'awaiting_approval',
    'approved',
];
export const SERVICE_STATE_LABELS = {
    not_applicable: 'Hizmet satırı yok',
    no_milestones: 'Aşama tanımlanmadı',
    in_progress: 'Hizmet sürüyor',
    awaiting_approval: 'Onay bekleyen aşama var',
    approved: 'Tüm aşamalar onaylandı',
};
export const MILESTONE_STATUSES = [
    'planned',
    'started',
    'delivered',
    'approved',
    'cancelled',
];
export const MILESTONE_STATUS_LABELS = {
    planned: 'Planlandı',
    started: 'Başladı',
    delivered: 'Teslim edildi',
    approved: 'Onaylandı',
    cancelled: 'İptal edildi',
};
/** İzinli geçişler: onay ayrı adımdır; teslim edilen aşama düzeltme için "başladı"ya dönebilir; kapanan aşama değişmez */
export const MILESTONE_TRANSITIONS = {
    planned: ['started', 'delivered', 'cancelled'],
    started: ['delivered', 'cancelled'],
    delivered: ['approved', 'started'],
    approved: [],
    cancelled: [],
};
export const NONCONFORMITY_RESOLUTIONS = ['return', 'discount', 'accept'];
export const NONCONFORMITY_RESOLUTION_LABELS = {
    return: 'İade',
    discount: 'İndirimle kabul',
    accept: 'Olduğu gibi kabul',
};
export const NONCONFORMITY_STATUSES = ['open', 'resolved', 'cancelled'];
export const NONCONFORMITY_STATUS_LABELS = {
    open: 'Açık',
    resolved: 'Çözüldü',
    cancelled: 'İptal edildi',
};
export const receiptInputSchema = z
    .object({
    lineNo: z.number().int().min(1).max(500),
    quantity: positiveQuantity,
    /** Kiracı takvim günü; verilmezse bugün (kiracı saat dilimi) */
    receivedOn: isoDate.optional(),
    note: optText(1000).optional(),
    requestKey: requestKeySchema,
})
    .strict();
export const reverseReceiptInputSchema = z
    .object({
    reason: z.string().trim().min(3, 'gerekçe en az 3 karakter').max(1000),
    requestKey: requestKeySchema,
})
    .strict();
export const milestoneInputSchema = z
    .object({
    lineNo: z.number().int().min(1).max(500),
    title: z.string().trim().min(1, 'aşama adı gerekli').max(200),
    plannedOn: isoDate.nullable().optional(),
    requestKey: requestKeySchema,
})
    .strict();
export const milestoneTransitionInputSchema = z
    .object({
    /** İstemcinin gördüğü durum: farklıysa 409 (eşzamanlı değişiklik); hedef zaten buysa değişiklik yok (çift tıklama) */
    from: z.enum(MILESTONE_STATUSES),
    to: z.enum(MILESTONE_STATUSES),
    note: optText(1000).optional(),
})
    .strict();
export const nonconformityInputSchema = z
    .object({
    lineNo: z.number().int().min(1).max(500),
    receiptId: uuid.nullable().optional(),
    quantity: positiveQuantity,
    reason: z.string().trim().min(3, 'neden en az 3 karakter').max(1000),
    requestKey: requestKeySchema,
})
    .strict();
export const resolveNonconformityInputSchema = z
    .object({
    status: z.enum(['resolved', 'cancelled']),
    resolution: z.enum(NONCONFORMITY_RESOLUTIONS).nullable().optional(),
    note: optText(1000).optional(),
})
    .strict()
    .refine((v) => (v.status === 'resolved') === (v.resolution !== undefined && v.resolution !== null), {
    message: 'çözüm kararı yalnız "çözüldü" durumunda ve zorunlu',
    path: ['resolution'],
});
export const fulfillmentLineSchema = z.object({
    lineNo: z.number().int(),
    revisionNo: z.number().int(),
    productId: z.string().nullable(),
    name: z.string(),
    quantity: z.string(),
    unitLabel: z.string().nullable(),
    /** Etkin tür: satır anlık görüntüsü → ürünün güncel türü → null (belirtilmemiş) */
    kind: z.enum(PRODUCT_KINDS).nullable(),
    kindSource: z.enum(['line', 'product']).nullable(),
    stockTracked: z.boolean(),
    /** Stok defterine yazılabilir mi (stoklu + satır birimi ürün birimiyle aynı) */
    stockEffect: z.enum(['writes_stock', 'no_stock', 'unit_mismatch']),
    received: z.string(),
    reversed: z.string(),
    returned: z.string(),
    /** Net teslim alınan = kabul − ters kayıt − iade */
    netReceived: z.string(),
    /** Eksik = sipariş − net teslim alınan (en az 0) */
    missing: z.string(),
});
export const receiptViewSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    kind: z.enum(['receipt', 'reversal']),
    quantity: z.string(),
    receivedOn: z.string(),
    note: z.string().nullable(),
    reversesId: z.string().nullable(),
    reversedById: z.string().nullable(),
    stockMovementId: z.string().nullable(),
    receivedBy: z.string().nullable(),
    receivedByName: z.string().nullable(),
    createdAt: z.string(),
});
export const milestoneEventSchema = z.object({
    from: z.enum(MILESTONE_STATUSES).nullable(),
    to: z.enum(MILESTONE_STATUSES),
    note: z.string().nullable(),
    actorName: z.string().nullable(),
    at: z.string(),
});
export const milestoneViewSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    title: z.string(),
    plannedOn: z.string().nullable(),
    status: z.enum(MILESTONE_STATUSES),
    statusChangedAt: z.string(),
    approvedByName: z.string().nullable(),
    approvedAt: z.string().nullable(),
    /** Bu durumdan izinli geçişler (çağıran yazabiliyorsa) */
    next: z.array(z.enum(MILESTONE_STATUSES)),
    history: z.array(milestoneEventSchema),
});
export const nonconformityViewSchema = z.object({
    id: z.string(),
    lineNo: z.number().int(),
    receiptId: z.string().nullable(),
    quantity: z.string(),
    reason: z.string(),
    status: z.enum(NONCONFORMITY_STATUSES),
    resolution: z.enum(NONCONFORMITY_RESOLUTIONS).nullable(),
    resolutionNote: z.string().nullable(),
    stockMovementId: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    resolvedByName: z.string().nullable(),
    resolvedAt: z.string().nullable(),
});
export const fulfillmentViewSchema = z.object({
    purchaseOrderId: z.string(),
    status: z.enum(['open', 'completed', 'cancelled']),
    today: z.string(),
    timezone: z.string(),
    canWrite: z.boolean(),
    receiptState: z.enum(RECEIPT_STATES),
    serviceState: z.enum(SERVICE_STATES),
    lines: z.array(fulfillmentLineSchema),
    receipts: z.array(receiptViewSchema),
    milestones: z.array(milestoneViewSchema),
    nonconformities: z.array(nonconformityViewSchema),
});
// ---------------------------------------------------------------------------------------------------------------------
// 5) Tedarik teklifleri ve karşılaştırma
// ---------------------------------------------------------------------------------------------------------------------
export const QUOTE_REQUEST_STATUSES = ['open', 'awarded', 'cancelled'];
export const QUOTE_REQUEST_STATUS_LABELS = {
    open: 'Teklif toplanıyor',
    awarded: 'Teklif seçildi',
    cancelled: 'İptal edildi',
};
export const quoteRequestInputSchema = z
    .object({
    title: z.string().trim().min(1, 'ihtiyaç adı gerekli').max(200),
    productId: uuid,
    quantity: positiveQuantity,
    neededBy: isoDate.nullable().optional(),
    note: optText(2000).optional(),
    requestKey: requestKeySchema,
})
    .strict();
export const supplierQuoteInputSchema = z
    .object({
    supplierId: uuid,
    unitPrice: money,
    currency: currencySchema,
    leadTimeDays: leadDays.nullable().optional(),
    quotedOn: isoDate.optional(),
    validUntil: isoDate.nullable().optional(),
    note: optText(1000).optional(),
    requestKey: requestKeySchema,
})
    .strict();
export const awardQuoteInputSchema = z
    .object({
    quoteId: uuid,
    reason: z.string().trim().min(3, 'seçim gerekçesi en az 3 karakter').max(1000),
    /** Seçilen fiyatı tedarikçi fiyat geçmişine ekle (kaynak: teklif) */
    recordPrice: z.boolean().default(true),
})
    .strict();
export const quoteViewSchema = z.object({
    id: z.string(),
    supplierId: z.string(),
    supplierName: z.string(),
    unitPrice: z.string(),
    currency: z.string(),
    /** Birim fiyat × ihtiyaç miktarı (aynı para biriminde; kur çevirisi yok) */
    lineTotal: z.string(),
    leadTimeDays: z.number().int().nullable(),
    quotedOn: z.string(),
    validUntil: z.string().nullable(),
    note: z.string().nullable(),
    status: z.enum(['active', 'withdrawn']),
    expired: z.boolean(),
    /** Kendi para birimi grubunda en düşük fiyatlı etkin teklif */
    lowestInCurrency: z.boolean(),
    /** Etkin teklifler içinde en kısa termin */
    shortestLead: z.boolean(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
});
export const quoteRequestSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    productId: z.string(),
    productName: z.string(),
    quantity: z.string(),
    unitLabel: z.string().nullable(),
    neededBy: z.string().nullable(),
    status: z.enum(QUOTE_REQUEST_STATUSES),
    quoteCount: z.number().int(),
    currencies: z.array(z.string()),
    purchaseOrderId: z.string().nullable(),
    createdAt: z.string(),
});
export const quoteRequestListSchema = z.object({
    items: z.array(quoteRequestSummarySchema),
    /** Duruma göre sayılar — listenin kendisinin toplamı (sayı = liste) */
    counts: z.partialRecord(z.enum(QUOTE_REQUEST_STATUSES), z.number().int()),
});
export const quoteRequestViewSchema = quoteRequestSummarySchema.extend({
    note: z.string().nullable(),
    today: z.string(),
    quotes: z.array(quoteViewSchema),
    /** Etkin tekliflerde birden çok para birimi: fiyatlar toplanmaz/karşılaştırılmaz */
    mixedCurrency: z.boolean(),
    award: z
        .object({
        quoteId: z.string().nullable(),
        reason: z.string(),
        awardedByName: z.string().nullable(),
        awardedAt: z.string(),
    })
        .nullable(),
    canWrite: z.boolean(),
});
/**
 * Teklif karşılaştırması (saf): para birimi başına en düşük birim fiyat; birden çok para birimi varsa `mixedCurrency` (kur kaynağı
 * olmadığı için çapraz karşılaştırma YAPILMAZ). Geri çekilen ve geçerliliği dolan teklif karşılaştırmaya girmez.
 */
export function compareQuotes(quotes, today) {
    const expired = new Set(quotes.filter((q) => q.validUntil !== null && q.validUntil < today).map((q) => q.id));
    const live = quotes.filter((q) => q.status === 'active' && !expired.has(q.id));
    const currencies = new Set(live.map((q) => q.currency));
    const lowest = new Set();
    for (const c of currencies) {
        const group = live.filter((q) => q.currency === c);
        let min = null;
        for (const q of group)
            if (min === null || decCmp(parseDecimal(q.unitPrice), parseDecimal(min)) < 0)
                min = q.unitPrice;
        for (const q of group)
            if (min !== null && decCmp(parseDecimal(q.unitPrice), parseDecimal(min)) === 0)
                lowest.add(q.id);
    }
    const leads = live.map((q) => q.leadTimeDays).filter((x) => x !== null);
    const minLead = leads.length > 0 ? Math.min(...leads) : null;
    const shortestLead = new Set(live.filter((q) => minLead !== null && q.leadTimeDays === minLead).map((q) => q.id));
    return { mixedCurrency: currencies.size > 1, lowest, shortestLead, expired };
}
/**
 * Teklifleri para birimi başına gruplar (saf). Sıralama YALNIZ grup içinde fiyatla yapılır; gruplar fiyata ya da toplama göre
 * sıralanmaz (kur kaynağı yok — farklı para birimindeki teklifler tek bir sıralamaya ya da toplama girmez). Grup sırası:
 * karşılaştırmada teklifi olan gruplar önce, sonra para birimi kodu. Dönüştürülmüş karşılaştırma ürün bugün GÖSTERMEZ: ancak açık
 * bir kur kaynağı ve kur tarihi tanımlanırsa (bugün yok) ayrıca, kaynağıyla birlikte gösterilebilir.
 */
export function groupQuotesByCurrency(quotes) {
    const live = (q) => q.status === 'active' && !q.expired;
    const byCurrency = new Map();
    for (const q of quotes)
        byCurrency.set(q.currency, [...(byCurrency.get(q.currency) ?? []), q]);
    const groups = [...byCurrency.entries()].map(([currency, list]) => ({
        currency,
        quotes: [...list].sort((a, b) => Number(live(b)) - Number(live(a)) ||
            decCmp(parseDecimal(a.unitPrice), parseDecimal(b.unitPrice))),
        liveCount: list.filter(live).length,
    }));
    return groups.sort((a, b) => Number(b.liveCount > 0) - Number(a.liveCount > 0) || a.currency.localeCompare(b.currency));
}
// ---------------------------------------------------------------------------------------------------------------------
// 6) Muhasebe bağlantı sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
// Olay şeması, imza, tekilleştirme ve sürüm kuralı TEK sözleşmededir: `external-source-contract.ts` (`sourceEventSchema`; tedarikçi
// siparişi belgesi `data.purchaseOrderId` ile). Alım `POST /hooks/sources/:tenantId/:sourceId` ya da onun ince sarmalayıcısı
// `POST /hooks/accounting/:sourceKey`. Burada yalnız tedarik ekranının görünümleri kalır.
export const accountingSourceViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(['active', 'disabled']),
    /** Alım adresinin yol parçası (sır değildir; kiracıyı ve kaynağı adlandırır) */
    sourceKey: z.string(),
    /** Sırrın son 4 karakteri (tanıma); bilinmiyorsa null */
    secretLast4: z.string().nullable(),
    createdAt: z.string(),
    lastEventAt: z.string().nullable(),
    disabledAt: z.string().nullable(),
});
export const accountingSourceListSchema = z.object({
    sources: z.array(accountingSourceViewSchema),
});
export const accountingSourceCreatedSchema = z.object({
    source: accountingSourceViewSchema,
    /** Yalnız bu yanıtta bir kez görünür */
    secret: z.string(),
});
export const accountingSourceInputSchema = z
    .object({ name: z.string().trim().min(1, 'ad gerekli').max(120) })
    .strict();
export const accountingDocumentViewSchema = z.object({
    kind: z.enum(['invoice', 'payment']),
    externalId: z.string(),
    number: z.string().nullable(),
    /** Tek sözleşmenin durumları: fatura `issued|paid|overdue|cancelled`, ödeme `verified|refunded` */
    status: z.enum(['issued', 'paid', 'overdue', 'cancelled', 'verified', 'refunded']),
    amount: z.string(),
    currency: z.string(),
    /** Fatura: düzenlenme günü; ödeme: ödeme anının kiracı saat dilimindeki günü (YYYY-AA-GG) */
    date: z.string(),
    dueOn: z.string().nullable(),
    receivedAt: z.string(),
});
/** Tedarikçi siparişinin muhasebe gerçeği: yalnız kaynaktan gelen doğrulanmış değer; yoksa neden */
export const purchaseAccountingViewSchema = z.discriminatedUnion('source', [
    z.object({ source: z.literal('none') }),
    z.object({
        source: z.literal('connected'),
        sourceName: z.string(),
        lastEventAt: z.string().nullable(),
        documents: z.array(accountingDocumentViewSchema),
        /** Para birimi başına toplamlar (iptal/ters kayıt hariç); sipariş toplamıyla karıştırılmaz */
        totals: z.array(z.object({ currency: z.string(), invoiced: z.string(), paid: z.string() })),
    }),
]);
//# sourceMappingURL=procurement.js.map